/**
 * Detect cURL code generator — a raw HTTPS `POST` to `/v1/detect`, writable
 * straight from the contract with no SDK. See PRO-954 §4 for the request shape.
 */

import { ReasoningEffort } from "../../apiContracts/chatCompletions";
import { DetectExportConfig, DetectOutput } from "./types";

/** The `POST /v1/detect` request body, with optional fields omitted when unset. */
interface DetectRequestBody {
  media: { type: "image"; image_url: string };
  categories?: string[];
  config?: {
    output?: DetectOutput;
    reasoning_effort?: ReasoningEffort;
  };
}

/**
 * Build the request body from the export config. `categories` is omitted when
 * empty (the gateway treats an absent list differently from `[]`), and `config`
 * carries only the fields the user actually set — the gateway fills the rest.
 */
function buildRequestBody(config: DetectExportConfig): DetectRequestBody {
  const body: DetectRequestBody = {
    media: { type: "image", image_url: config.imageUrl },
  };

  if (config.categories && config.categories.length > 0) {
    body.categories = config.categories;
  }

  const detectConfig: NonNullable<DetectRequestBody["config"]> = {};
  if (config.output !== undefined) detectConfig.output = config.output;
  if (config.reasoningEffort !== undefined) detectConfig.reasoning_effort = config.reasoningEffort;
  if (Object.keys(detectConfig).length > 0) {
    body.config = detectConfig;
  }

  return body;
}

/** Generate a cURL command that calls the public Detect API. */
export function generateCurl(config: DetectExportConfig): string {
  const jsonBody = JSON.stringify(buildRequestBody(config), null, 2);
  // Escape single quotes for shell safety inside the `-d '...'` argument.
  const escapedJson = jsonBody.replace(/'/g, "'\\''");
  const apiKey = config.apiKey || "<your_api_key_here>";

  return `curl -X POST "${config.apiUrl}" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -d '${escapedJson}'`;
}
